import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { TrimPatternValidator } from 'src/app/shared/validators/trim-pattern/trim-pattern-validator';

import { InvitationAccessLevel, InvitationStatusType } from '@vi-iot/client';

import { PATTERN } from '@common';
import {
  BulkSaveBuildingMangerInvitationsPayload,
  InstallationRoleManagementInvitation,
  InstallationInvitationToCreate,
  InstallationInvitationToUpdate,
  InvitationsStore,
} from '@store';
import { RoleManagementForm } from './role-management-modal.helper';

export enum RoleManagementSaveAction {
  DoNothing,
  CreateNewInvitation,
  CancelPendingInvitation,
  TerminateInvitation,
  ChangePendingInvitationEmail,
  ChangeExistingInvitationEmail,
}
@Component({
  selector: 'app-role-management-modal',
  templateUrl: './role-management-modal.component.html',
  styleUrls: ['./role-management-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleManagementModalComponent implements OnInit, OnDestroy {
  roleManagementForm: FormGroup<RoleManagementForm>;
  invitationsLoaded$: Observable<boolean>;

  private roleManagementInvitations: InstallationRoleManagementInvitation[];
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<RoleManagementModalComponent>,
    private invitationsStore: InvitationsStore,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      installationId: number;
    }
  ) {
    this.invitationsStore.bulkListInvitations(this.dialogData.installationId);
  }

  ngOnInit(): void {
    this.invitationsLoaded$ = this.invitationsStore.invitationsLoaded$;
    this.invitationsLoaded$
      .pipe(
        switchMap(() =>
          this.invitationsStore.getInstallationRoleManagementInvitations(
            this.dialogData.installationId
          )
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((invitations) => {
        this.roleManagementInvitations = invitations.map((entry) => ({
          email: entry.inviteeEmail,
          status: entry.status,
          accessLevel: entry.accessLevel,
          invitationToken: entry.token,
        }));
        this.constructRoleManagementForm();
      });
  }

  getRoleManagementInvitationAsignee(
    role: InvitationAccessLevel
  ): InstallationRoleManagementInvitation {
    return this.roleManagementInvitations.find((entry) => {
      const accessLevelMatch = entry.accessLevel === role;
      const statusMatch =
        entry.status === InvitationStatusType.Accepted ||
        entry.status === InvitationStatusType.Pending;

      return accessLevelMatch && statusMatch;
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  clearInput(formType: 'operator' | 'serviceContractor'): void {
    this.roleManagementForm.controls[formType].patchValue(null);
    this.roleManagementForm.controls[formType].markAsDirty();
  }

  saveChanges(): void {
    const rolesToCheck: {
      role: InvitationAccessLevel;
      invitation: InstallationRoleManagementInvitation;
      form: AbstractControl;
    }[] = [
      {
        role: InvitationAccessLevel.Operator,
        invitation: this.getRoleManagementInvitationAsignee(InvitationAccessLevel.Operator),
        form: this.roleManagementForm.controls.operator,
      },
      {
        role: InvitationAccessLevel.ServiceContractor,
        invitation: this.getRoleManagementInvitationAsignee(
          InvitationAccessLevel.ServiceContractor
        ),
        form: this.roleManagementForm.controls.serviceContractor,
      },
    ];

    const invitationsToUpdate: InstallationInvitationToUpdate[] = [];
    const invitationsToCreate: InstallationInvitationToCreate[] = [];

    rolesToCheck.forEach((entry) => {
      const saveAction = this.getRoleManagementSaveAction(entry.role, entry.form);
      const formValue = entry.form.value ? entry.form.value.trim() : null;

      switch (saveAction) {
        case RoleManagementSaveAction.CreateNewInvitation:
          invitationsToCreate.push({
            installationId: this.dialogData.installationId + '',
            inviteeEmail: formValue,
            accessLevel: entry.role,
          });
          break;

        case RoleManagementSaveAction.TerminateInvitation:
          invitationsToUpdate.push({
            token: entry.invitation.invitationToken,
            status: InvitationStatusType.Terminated,
          });
          break;

        case RoleManagementSaveAction.CancelPendingInvitation:
          invitationsToUpdate.push({
            token: entry.invitation.invitationToken,
            status: InvitationStatusType.Canceled,
          });
          break;

        case RoleManagementSaveAction.ChangeExistingInvitationEmail:
          invitationsToUpdate.push({
            token: entry.invitation.invitationToken,
            status: InvitationStatusType.Terminated,
          });
          invitationsToCreate.push({
            installationId: this.dialogData.installationId + '',
            inviteeEmail: formValue,
            accessLevel: entry.role,
          });

          break;
        case RoleManagementSaveAction.ChangePendingInvitationEmail:
          invitationsToUpdate.push({
            token: entry.invitation.invitationToken,
            status: InvitationStatusType.Canceled,
          });

          invitationsToCreate.push({
            installationId: this.dialogData.installationId + '',
            inviteeEmail: formValue,
            accessLevel: entry.role,
          });

          break;

        case RoleManagementSaveAction.DoNothing:
        default:
          break;
      }
    });

    const buildSaveInvitations: BulkSaveBuildingMangerInvitationsPayload = {
      invitationsToCreate,
      invitationsToUpdate,
    };

    const invitationsPresent =
      !!buildSaveInvitations.invitationsToCreate.length ||
      !!buildSaveInvitations.invitationsToUpdate.length;
    if (invitationsPresent) {
      this.invitationsStore.bulkSaveBuildingManagerInvitations(buildSaveInvitations);
    }

    this.dialogRef.close();
  }

  private getRoleManagementSaveAction(
    role: InvitationAccessLevel,
    form: AbstractControl
  ): RoleManagementSaveAction {
    const invitation = this.getRoleManagementInvitationAsignee(role);

    const noChange = invitation?.email === form.value;
    if (noChange) {
      return RoleManagementSaveAction.DoNothing;
    }

    const isNewInvitation =
      (invitation === undefined && !!form.value) ||
      invitation?.status === InvitationStatusType.Expired ||
      invitation?.status === InvitationStatusType.Rejected ||
      invitation?.status === InvitationStatusType.Terminated;

    if (isNewInvitation) {
      return RoleManagementSaveAction.CreateNewInvitation;
    }

    if (!invitation) {
      return RoleManagementSaveAction.DoNothing;
    }

    const isTerminateInvitation =
      invitation.status === InvitationStatusType.Accepted &&
      !!invitation.email &&
      form.value == null;
    if (isTerminateInvitation) {
      return RoleManagementSaveAction.TerminateInvitation;
    }

    const isCancelPendingInvitation =
      invitation.status === InvitationStatusType.Pending &&
      !!invitation.email &&
      form.value == null;
    if (isCancelPendingInvitation) {
      return RoleManagementSaveAction.CancelPendingInvitation;
    }

    const isPendingEmailChange =
      invitation.status === InvitationStatusType.Pending && invitation.email !== form.value;
    if (isPendingEmailChange) {
      return RoleManagementSaveAction.ChangePendingInvitationEmail;
    }

    const isExistingEmailChange =
      invitation.status === InvitationStatusType.Accepted && invitation.email !== form.value;
    if (isExistingEmailChange) {
      return RoleManagementSaveAction.ChangeExistingInvitationEmail;
    }

    return RoleManagementSaveAction.DoNothing;
  }

  private constructRoleManagementForm(): void {
    const operator = this.getRoleManagementInvitationAsignee(InvitationAccessLevel.Operator);
    const serviceContractor = this.getRoleManagementInvitationAsignee(
      InvitationAccessLevel.ServiceContractor
    );

    this.roleManagementForm = new FormGroup({
      operator: new FormControl<string>(operator?.email, [TrimPatternValidator(PATTERN.EMAIL)]),
      serviceContractor: new FormControl<string>(serviceContractor?.email, [
        TrimPatternValidator(PATTERN.EMAIL),
      ]),
    });
  }

  areDuplicatedEmails(): boolean {
    const isAnyInputEmpty =
      !this.roleManagementForm.controls.operator.value ||
      !this.roleManagementForm.controls.serviceContractor.value;
    const duplicatedEmailValues =
      this.roleManagementForm.controls.operator.value ===
      this.roleManagementForm.controls.serviceContractor.value;

    return !isAnyInputEmpty && duplicatedEmailValues;
  }

  isSaveButtonDisabled(): boolean {
    const isFormValid = this.roleManagementForm.valid;

    return !isFormValid || this.areDuplicatedEmails();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
